import { TenantRoleDto } from '@admin/app/api'
import { Card, CardContent, FormControl, Stack, TextField, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { FormField, IFormStore } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { RoleFormData } from '../../models/role.model'
import { PermissionsAutoComplete } from '../molecules/PermissionsAutoComplete'

interface Props {
  formStore: IFormStore<RoleFormData>
  permissions: string[]
  role?: TenantRoleDto
}

export const RoleForm = observer(({ formStore, permissions, role }: Props) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  return (
    <Card>
      <CardContent>
        <Stack component='form' noValidate autoComplete='off' gap={theme.customSpacing.l}>
          <Stack flexDirection='row' justifyContent='space-between' alignItems='center'>
            <Typography variant='subtitle1'>{t('roleDetails', 'Role details')}</Typography>
          </Stack>

          <FormField
            formStore={formStore}
            name={'name'}
            initialValue={role?.name ?? ''}
            validate={value => {
              if (!value) return t('fieldIsRequired', 'Field is required')
            }}
          >
            {store => (
              <Stack gap={theme.customSpacing.s}>
                <TextField
                  {...store.props}
                  fullWidth
                  label={`${t('name', 'Name')}`}
                  required
                  name='name'
                />
                <Typography variant='caption' color={store.error ? 'error' : 'text.secondary'}>
                  {store.error ||
                    t('pleaseMakeSureItsAUniqueField', "Please make sure it's a unique {field}", {
                      field: t('name', 'name'),
                    })}
                </Typography>
              </Stack>
            )}
          </FormField>

          <FormField
            formStore={formStore}
            name={'description'}
            initialValue={role?.description ?? ''}
            validate={value => {
              if (!value) return t('fieldIsRequired', 'Field is required')
            }}
          >
            {store => (
              <Stack gap={theme.customSpacing.s}>
                <TextField
                  {...store.props}
                  fullWidth
                  label={`${t('description', 'Description')}`}
                  required
                  name='description'
                />
                {!!store.error && (
                  <Typography variant='caption' color='error'>
                    {store.error}
                  </Typography>
                )}
              </Stack>
            )}
          </FormField>

          <FormField<RoleFormData, string[]>
            formStore={formStore}
            name={'permissions'}
            initialValue={role?.permissions ?? []}
            validate={value =>
              !value.length ? t('fieldIsRequired', 'Field is required') : undefined
            }
          >
            {store => (
              <FormControl fullWidth>
                <Stack gap={theme.customSpacing.s}>
                  <PermissionsAutoComplete
                    multiple
                    required
                    defaultValue={role?.permissions ?? []}
                    error={!!store.error}
                    onChange={store.setValue}
                    options={permissions}
                  />
                  {!!store.error && (
                    <Typography variant='caption' color='error'>
                      {store.error}
                    </Typography>
                  )}
                </Stack>
              </FormControl>
            )}
          </FormField>
        </Stack>
      </CardContent>
    </Card>
  )
})
